import { RotateCcw } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { PreviewTable } from '../../../../components/PreviewTable';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import type { PpmpDetail, PpmpLineItemDto } from '../ppmpTypes';

/**
 * One PPMP's procurement lines.
 *
 * **A `<DataTablePanel>`, not a `<PreviewGrid>`**, on that shell's own test: a collection
 * WRITTEN from where it is drawn is a panel always. These lines are corrected, deleted and
 * restored from here, and a preview writes nothing by contract — putting Edit and Delete into
 * a preview's cells would give up the search, the sort, the paging, the `#` gutter and
 * `<RowActionMenu>`'s permission-filtered right-click for nothing.
 *
 * **The TOTAL sits BELOW the table as a Table Display View** — the SALN folder's shape: a
 * read-only fact being shown rather than a value being asked for, under the rows it adds up.
 */
export function PpmpLineItemsPanel({
  ppmp,
  showDeleted,
  onToggleDeleted,
  onAdd,
  onEdit,
  onDelete,
  onRestore,
}: {
  ppmp: PpmpDetail;
  showDeleted: boolean;
  onToggleDeleted: () => void;
  onAdd: () => void;
  onEdit: (line: PpmpLineItemDto) => void;
  onDelete: (line: PpmpLineItemDto) => void;
  onRestore: (line: PpmpLineItemDto) => void;
}) {
  /*
    `useMemo` the row source, ABOVE any early return — a `?? []` fallback is a new array every
    render, so the effect re-runs, calls `setRows`, re-renders and loops until React stops it.
  */
  const lines = useMemo(() => ppmp.lineItems, [ppmp.lineItems]);

  /*
    Searched, sorted and paged IN MEMORY. The lines arrive WHOLE with the record, so the filter
    sees every one there is — the 201 file's own exemption, and what would end it is paging
    this server-side.
  */
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? lines.filter(
            (line) =>
              line.itemDescriptionText.toLowerCase().includes(term) ||
              line.procurementTypeName.toLowerCase().includes(term) ||
              line.modeOfProcurementName.toLowerCase().includes(term) ||
              line.unit.toLowerCase().includes(term),
          )
        : lines;

      const field = query.sort?.replace(/^-/, '') ?? 'sortOrder';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        const byField =
          field === 'itemDescriptionText'
            ? a.itemDescriptionText.localeCompare(b.itemDescriptionText)
            : field === 'estimatedBudget'
              ? a.estimatedBudget - b.estimatedBudget
              : field === 'quantity'
                ? a.quantity - b.quantity
                : a.sortOrder - b.sortOrder;

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      });
    },
    [lines],
  );

  const state = useDataTable<PpmpLineItemDto>({
    fetchPage,
    // The order the SERVER numbered them in, so the panel and the record agree about which
    // line is first.
    defaultSort: 'sortOrder',
  });

  const rowActions = useCallback(
    (line: PpmpLineItemDto): MenuItem[] => [
      {
        key: `restore-${line.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'ppmps.update',
        disabledReason: line.isActive ? 'This line is already active.' : null,
        onClick: () => onRestore(line),
      },
    ],
    [onRestore],
  );

  // The LIVE lines only — the figure never moves with Show deleted, which is the relationship
  // every Show-deleted list has with its own counts.
  const total = lines
    .filter((line) => line.isActive)
    .reduce((sum, line) => sum + line.estimatedBudget, 0);

  return (
    <div className="flex h-full min-w-0 flex-col gap-4">
      <DataTablePanel<PpmpLineItemDto>
        id="ppmp-lines"
        state={state}
        columns={COLUMNS}
        rowKey={(line) => line.id}
        fill
        /*
          A line is a CHILD of the plan and carries its own creation trail, so the panel offers
          the audit trio and the list view that leads with it.
        */
        otherInfo={(line) => ({
          createdAt: ppmp.createdAt,
          createdByName: ppmp.createdByName,
          updatedAt: ppmp.updatedAt,
          updatedByName: ppmp.updatedByName,
          isActive: line.isActive,
        })}
        auditColumns={false}
        onEdit={(line) => onEdit(line)}
        editPermission="ppmps.update"
        onDelete={(line) => onDelete(line)}
        deletePermission="ppmps.update"
        deleteDisabledReason={(line) =>
          !line.isActive
            ? 'This line is already deleted.'
            : ppmp.isEditable
              ? null
              : 'The submission window for this plan has closed.'
        }
        rowActions={rowActions}
        onCreate={onAdd}
        createLabel="Add lines"
        createPermission="ppmps.update"
        createDisabledReason={
          ppmp.isActive
            ? ppmp.isEditable
              ? null
              : 'The submission window for this plan has closed.'
            : 'This PPMP is deleted. Restore it before adding lines.'
        }
        /*
          A panel offering Show deleted OWES a `status: true` column — otherwise the deleted
          rows arrive wearing no marker at all, which is the one state a row must never be
          silent about.
        */
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            checked: showDeleted,
            onClick: onToggleDeleted,
          },
        ]}
        searchPlaceholder="Search specifications, types or modes"
        emptyMessage="No procurement lines yet. Add what this plan intends to buy."
        emptySearchMessage="No line matches that search."
      />

      <PreviewTable
        variant="table-display"
        rows={[{ label: 'Estimated budget', value: formatMoney(total), mono: true }]}
      />
    </div>
  );
}

const COLUMNS: DataColumn<PpmpLineItemDto>[] = [
  {
    key: 'itemDescriptionText',
    header: 'Specification',
    width: 320,
    sortable: true,
    /*
      The DERIVED PLAIN TEXT, never the markup. A table cell renders one line, and the stored
      HTML would print its tags as text — which is what the derived column exists for.
    */
    render: (line) => <TextCell value={line.itemDescriptionText} />,
  },
  {
    key: 'procurementTypeName',
    header: 'Type',
    width: 160,
    summary: true,
    // The SNAPSHOT, not the join — what the line reads as once the catalog row is renamed.
    render: (line) => <TextCell value={line.procurementTypeName} />,
  },
  {
    key: 'modeOfProcurementName',
    header: 'Mode',
    width: 170,
    summary: true,
    render: (line) => <TextCell value={line.modeOfProcurementName} />,
  },
  {
    key: 'quantity',
    header: 'Qty',
    width: 90,
    align: 'right',
    sortable: true,
    render: (line) => <TextCell value={String(line.quantity)} mono numeric />,
  },
  {
    key: 'unit',
    header: 'Unit',
    width: 100,
    render: (line) => <TextCell value={line.unit} />,
  },
  {
    key: 'estimatedBudget',
    header: 'Est. budget',
    width: 160,
    align: 'right',
    sortable: true,
    footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.estimatedBudget, 0)),
    render: (line) => <TextCell value={formatMoney(line.estimatedBudget)} mono numeric />,
  },
  {
    key: 'expectedDeliveryDate',
    header: 'Delivery',
    width: 130,
    // A CALENDAR date, so `formatDateOnly` through a mono TextCell — never `<DateCell>`, which
    // renders an instant in the viewer's zone.
    render: (line) => (
      <TextCell
        value={line.expectedDeliveryDate ? formatDateOnly(line.expectedDeliveryDate) : null}
        mono
      />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (line) =>
      line.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
